import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Box, Lock, Loader2, AlertCircle, CheckCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAdminAuth } from "../context/AdminAuthContext";

const ChangePassword = () => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const { admin, changePassword } = useAdminAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      await changePassword(currentPassword, newPassword);
      setSuccess(true);
      setTimeout(() => {
        navigate("/admin/dashboard");
      }, 2000);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Failed to change password";
      setError(typeof errorMsg === "string" ? errorMsg : "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center px-4 overflow-x-hidden" data-testid="change-password-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-6 sm:mb-8">
          <div className="flex items-center justify-center gap-2 mb-3 sm:mb-4">
            <Box className="w-6 h-6 sm:w-8 sm:h-8 text-blue-500" />
            <span className="text-xl sm:text-2xl font-bold tracking-tight text-white" style={{ fontFamily: "Chivo, sans-serif" }}>
              ParamSnap
            </span>
          </div>
          <h1 className="text-lg sm:text-xl font-semibold text-white" style={{ fontFamily: "Chivo, sans-serif" }}>
            Change Password
          </h1>
          {admin?.must_change_password && (
            <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-yellow-400 text-left">
                You must change your password before accessing the dashboard.
              </p>
            </div>
          )}
        </div>

        <div className="bento-card p-5 sm:p-8">
          {success ? (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-6"
            >
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: "Chivo, sans-serif" }}>
                Password Changed!
              </h3>
              <p className="text-sm text-zinc-400">
                Redirecting to dashboard...
              </p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="currentPassword" className="text-xs sm:text-sm text-zinc-300">
                  Current Password
                </Label>
                <Input
                  id="currentPassword"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  required
                  className="mt-1 bg-[#0A0A0B] border-white/10 text-white placeholder:text-zinc-600 focus:border-blue-500 text-sm sm:text-base h-10 sm:h-11"
                  data-testid="current-password-input"
                />
              </div>

              <div>
                <Label htmlFor="newPassword" className="text-xs sm:text-sm text-zinc-300">
                  New Password
                </Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimum 8 characters"
                  required
                  minLength={8}
                  className="mt-1 bg-[#0A0A0B] border-white/10 text-white placeholder:text-zinc-600 focus:border-blue-500 text-sm sm:text-base h-10 sm:h-11"
                  data-testid="new-password-input"
                />
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="text-xs sm:text-sm text-zinc-300">
                  Confirm New Password
                </Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  className="mt-1 bg-[#0A0A0B] border-white/10 text-white placeholder:text-zinc-600 focus:border-blue-500 text-sm sm:text-base h-10 sm:h-11"
                  data-testid="confirm-password-input"
                />
              </div>

              {error && (
                <p className="text-xs sm:text-sm text-red-400 text-center">{error}</p>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full btn-primary py-2.5 sm:py-3 rounded-lg font-medium flex items-center justify-center gap-2 text-sm sm:text-base"
                data-testid="change-password-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                    Changing Password...
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4 sm:w-5 sm:h-5" />
                    Change Password
                  </>
                )}
              </Button>
            </form>
          )}
        </div>

        <p className="text-center text-[10px] sm:text-xs text-zinc-500 mt-5 sm:mt-6">
          Secure admin area · ParamSnap
        </p>
      </motion.div>
    </div>
  );
};

export default ChangePassword;
